import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomerSharedService } from '../customer-shared.service';
import { CustomerService } from '../customer.service';
import { CommonService } from '../../../shared/services/common.service';
import { CustomerFeaturesComponent } from '../components/customer-features/customer-features.component';
import { ApplicationConfigService, ApplicationConfig } from '../../../shared/services/application-config.service';
import { ApplicationContextService } from '../../../shared/services/application-context.service';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-customerDetails',
  templateUrl: './customerDetails.component.html',
  styleUrls: ['./customerDetails.component.scss']
})
export class CustomerDetailsComponent implements OnInit {
  readonly noActionComplianceValue = 'no-action';
  complianceLevelOptions = [
    { value: 'high', label: 'High' },
    { value: 'low', label: 'Low' },
    { value: 'medium', label: 'Medium' },
    { value: 'no-action', label: 'No Action Required' },
  ];
  @ViewChild('customerFeatures') customerFeaturesComponent!: CustomerFeaturesComponent;

  id: string;
  customer: any;
  selectedTabIndex: number = 7;
  isConfirmLoading = false;
  allTabs: string[] = ['Assets', 'Devices', 'Incidents', 'Users', 'Asset Sensor Mapping', 'Location Management', 'Reports', 'Master'];
  availableTabs: string[] = [];
  selectedTab: string = '';

  // Application selector
  selectedApplicationId: number | null = null;
  applicationConfigs: ApplicationConfig[] = [];
  customerApplications: any[] = []; // Applications the customer has access to
  customerApplicationIds: number[] = [];

  // Edit Customer variables
  isVisible: boolean = false;
  isEditMode: boolean = false;
  form!: FormGroup;
  applications: any[] = [];
  admin_name = localStorage.getItem('admin_name');

  constructor(
    public customerSharedService: CustomerSharedService,
    public customerService: CustomerService,
    public route: ActivatedRoute,
    public commonService: CommonService,
    private router: Router,
    private fb: FormBuilder,
    private applicationConfigService: ApplicationConfigService,
    private applicationContextService: ApplicationContextService
  ) {
    this.id = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit(): void {
    this.getCustomerById();
    this.initializeForm();
    this.getApplications();
    this.loadApplicationConfigs();
    this.subscribeToApplicationContext();
  }

  subscribeToApplicationContext() {
    this.applicationContextService.selectedApplicationId$.pipe(untilDestroyed(this)).subscribe(applicationId => {
      this.selectedApplicationId = applicationId;
      this.updateAvailableTabs();
    });
  }

  initializeForm() {
    this.form = this.fb.group({
      company_name: [null, Validators.required],
      contact_person: [null, Validators.required],
      contact_email: [null, [Validators.required, Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$')]],
      contact_number: [null, [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      compliance_level: [[]],
      application_id: [[], Validators.required],
    });

    this.form.get('compliance_level')?.valueChanges.subscribe((selectedValues: string[] | null) => {
      this.normalizeComplianceLevelSelection(selectedValues);
    });
  }

  isComplianceOptionDisabled(optionValue: string): boolean {
    const selectedValues: string[] = this.form?.get('compliance_level')?.value || [];
    const hasNoAction = selectedValues.includes(this.noActionComplianceValue);
    const hasOtherValues = selectedValues.some(value => value !== this.noActionComplianceValue);

    if (optionValue === this.noActionComplianceValue) {
      return hasOtherValues;
    }
    return hasNoAction;
  }

  private normalizeComplianceLevelSelection(selectedValues: string[] | null): void {
    const values = Array.isArray(selectedValues) ? selectedValues : [];
    const hasNoAction = values.includes(this.noActionComplianceValue);
    const hasOtherValues = values.some(value => value !== this.noActionComplianceValue);

    if (hasNoAction && hasOtherValues) {
      this.form.get('compliance_level')?.setValue([this.noActionComplianceValue], { emitEvent: false });
    }
  }

  getApplications() {
    this.customerService.getApplications({}).pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => {
        if (response?.data) {
          this.applications = response.data;
          this.syncCustomerApplications();
        }
      },
      error: (error: any) => {
        console.error('Error fetching applications:', error);
      }
    });
  }

  loadApplicationConfigs() {
    this.applicationConfigs = this.applicationConfigService.getApplicationConfigs();
  }

  private syncCustomerApplications() {
    if (!this.customerApplicationIds.length) {
      this.customerApplications = [];
      this.selectedApplicationId = null;
      return;
    }

    this.customerApplications = this.applications.filter((app: any) =>
      this.customerApplicationIds.includes(Number(app.id))
    );
    this.initializeDefaultApplication();
  }

  private normalizeApplicationIds(applicationId: unknown): number[] {
    if (Array.isArray(applicationId)) {
      return applicationId.map((id: any) => Number(id)).filter((id: number) => !Number.isNaN(id));
    }
    if (applicationId !== null && applicationId !== undefined) {
      const parsed = Number(applicationId);
      return Number.isNaN(parsed) ? [] : [parsed];
    }
    return [];
  }

  initializeDefaultApplication() {
    // Set default to first available customer application if none selected
    if (!this.selectedApplicationId && this.customerApplications.length > 0) {
      this.selectedApplicationId = this.customerApplications[0].id;
      this.onApplicationChange(this.selectedApplicationId);
    }
  }

  getAvailableApplicationConfigs(): ApplicationConfig[] {
    // Filter application configs to only show those the customer has access to
    return this.applicationConfigs.filter(config =>
      this.customerApplications.some(customerApp => customerApp.id === config.id)
    );
  }

  onApplicationChange(applicationId: number | null) {
    this.selectedApplicationId = applicationId;

    if (applicationId) {
      // Verify the customer has access to this application
      const hasAccess = this.customerApplications.some(app => app.id === applicationId);
      if (!hasAccess) {
        console.warn(`Customer does not have access to application ID: ${applicationId}`);
        return;
      }

      this.applicationContextService.setSelectedApplicationId(applicationId);
      this.updateAvailableTabs();

      console.log(`Application changed to: ${this.getSelectedApplicationName()} (ID: ${applicationId})`);
      console.log(`Available tabs:`, this.availableTabs);
      console.log(`x-app-id header will be set to: ${applicationId}`);
    } else {
      // Clear application context
      this.applicationContextService.clearSelectedApplication();
      this.availableTabs = this.allTabs;
      this.selectedTabIndex = 7; // Default to Master tab
      this.selectedTab = this.allTabs[this.selectedTabIndex];
    }
  }

  updateAvailableTabs() {
    if (this.selectedApplicationId) {
      this.availableTabs = this.applicationConfigService.getAvailableTabsForApplication(this.selectedApplicationId);
    } else {
      this.availableTabs = this.allTabs;
    }

    // Reset to first available tab if current tab is not available
    const currentTabName = this.allTabs[this.selectedTabIndex];
    if (!this.availableTabs.includes(currentTabName)) {
      this.selectedTabIndex = 0;
      this.selectedTab = this.availableTabs[0] || '';
    } else {
      // Update selected tab to match the available tabs array
      this.selectedTabIndex = this.availableTabs.indexOf(currentTabName);
      this.selectedTab = currentTabName;
    }
  }

  getSelectedApplicationName(): string {
    if (!this.selectedApplicationId) return 'No Application Selected';

    // First check customer applications (more reliable as it comes from API)
    const customerApp = this.customerApplications.find(app => app.id === this.selectedApplicationId);
    if (customerApp) return customerApp.name;

    // Fallback to config service
    const config = this.applicationConfigService.getApplicationConfig(this.selectedApplicationId);
    return config ? config.name : 'Unknown Application';
  }

  onTabChange(index: number): string | undefined {
    if (typeof index === 'number' && index >= 0 && index < this.availableTabs.length) {
      this.selectedTabIndex = index;
      this.selectedTab = this.availableTabs[index]; // Use available tabs instead of all tabs
      console.log(this.selectedTab, "selected Tab");
      return this.selectedTab;
    } else {
      console.error("Invalid tab index:", index);
      return undefined;
    }
  }

  getTabComponentIndex(tabIndex: number): number {
    // Map the available tab index to the original component index
    const tabName = this.availableTabs[tabIndex];
    return this.allTabs.indexOf(tabName);
  }

  getCustomerById() {
    this.customerSharedService.getCustomerById(this.id).pipe(untilDestroyed(this)).subscribe(response => {
      this.customer = response;
      this.customerApplicationIds = this.normalizeApplicationIds(this.customer?.application_id);
      this.syncCustomerApplications();
    });
  }

  deactivateCustomer() {
    const isCurrentlyActive = this.customer?.is_active;
    const payload = {
      is_active: !isCurrentlyActive,
      last_update_user: this.admin_name
    };
    this.customerService.updateCustomer(this.customer.customer_id, payload).pipe(untilDestroyed(this)).subscribe(response => {
      if (response.status === 200) {
        const action = isCurrentlyActive ? 'deactivated' : 'activated';
        this.commonService.message.success(`Customer ${action} successfully`);
        this.getCustomerById();
      } else {
        this.commonService.message.error(response.message);
      }
    });
  }

  editCustomer(): void {
    this.isEditMode = true;

    if (this.customer) {
      this.form.patchValue({
        company_name: this.customer?.company_name,
        contact_person: this.customer?.contact_person,
        contact_email: this.customer?.contact_email,
        contact_number: this.customer?.contact_number,
        compliance_level: this.commonService.parseJsonArray(this.customer?.compliance_level),
        application_id: Array.isArray(this.customer?.application_id) ? this.customer.application_id : (this.customer?.application_id ? [this.customer.application_id] : [])
      });
    }
    this.isVisible = true;
  }

  onSubmit(): void {
    if (this.isConfirmLoading || !this.commonService.validate(this.form)) return;

    this.isConfirmLoading = true;
    const apiCall = this.handleUpdate();

    if (!apiCall) {
      this.isConfirmLoading = false;
      return;
    }

    apiCall.pipe(untilDestroyed(this)).subscribe({
      next: (response: any) => this.handleSuccess(response),
      error: (error: any) => this.handleError(error)
    });
  }

  private getFormData(): any {
    return {
      company_name: this.form.value.company_name,
      contact_person: this.form.value.contact_person,
      contact_email: this.form.value.contact_email,
      contact_number: this.form.value.contact_number,
      compliance_level: this.form.value.compliance_level,
      application_id: this.form.value.application_id,
    };
  }

  private getOriginalData(): any {
    return {
      company_name: this.customer?.company_name,
      contact_person: this.customer?.contact_person,
      contact_email: this.customer?.contact_email,
      contact_number: this.customer?.contact_number,
      compliance_level: this.commonService.parseJsonArray(this.customer?.compliance_level),
      application_id: Array.isArray(this.customer?.application_id)
        ? this.customer.application_id
        : (this.customer?.application_id ? [this.customer.application_id] : []),
    };
  }

  private handleUpdate() {
    const changedFields = this.commonService.getChangedFields(this.getOriginalData(), this.getFormData());

    if (Object.keys(changedFields).length === 0) {
      this.commonService.message.info('No changes detected');
      return null;
    }

    changedFields['last_update_user'] = this.admin_name;
    return this.customerService.updateCustomer(this.customer.customer_id, changedFields);
  }

  private handleSuccess(response: any): void {
    if (response?.status === 200) {
      this.commonService.message.success('Customer updated successfully');
      this.isVisible = false;
      this.getCustomerById();
    } else {
      this.commonService.message.error(response?.message || 'Failed to update customer');
    }
    this.isConfirmLoading = false;
  }

  private handleError(error: any): void {
    this.commonService.message.error('An error occurred while updating the customer');
    console.error('Error:', error);
    this.isConfirmLoading = false;
  }

  openFeatures(): void {
    if (this.customerFeaturesComponent) {
      this.customerFeaturesComponent.openFeatureSelectionModal();
    }
  }

}
